import React from 'react';

import cn from 'classnames';
import { noop } from 'lodash';
import { bool, func, node, number, oneOfType, shape, string } from 'prop-types';

import { isAbsoluteLink } from 'utils/urls';

import v from 'components/variables';

import s from './Card.css';

/**
 * Used for displaying UI in a contained, elevated surface
 *
 * @param {string} backgroundColor - optional background color of card
 * @param {node} children - content to be rendered on card
 * @param {string} className - used for styling card
 * @param {string} component - optional DOM node to be used instead of a section
 * @param {string} href - internal or external link
 * @param {func} onClick - function fired when card is clicked
 * @param {bool} shadow - if false, no box-shadow is applied
 * @param {Object} style - additional styles passed via JavaScript
 * @param {bool} rounded - if false, no border-radius is applied
 * @param {string | number} width - defaults to 100% of parent container
 */
const Card = React.forwardRef(
  (
    {
      backgroundColor,
      children,
      className,
      component,
      href,
      onClick,
      shadow,
      style,
      rounded,
      width,
    },
    ref,
  ) => {
    const Component = component;

    return (
      <Component
        className={cn(className, { [s.shadow]: shadow, [s.rounded]: rounded })}
        href={href}
        onClick={onClick}
        ref={ref}
        rel={href && isAbsoluteLink(href) ? 'noreferrer' : null}
        style={{ backgroundColor, width, ...style }}
        target={href && isAbsoluteLink(href) ? '_blank' : null}
        to={href}
      >
        {children}
      </Component>
    );
  },
);

Card.propTypes = {
  backgroundColor: string,
  children: node.isRequired,
  className: string,
  component: string,
  href: string,
  onClick: func,
  shadow: bool,
  style: shape({}),
  rounded: bool,
  width: oneOfType([string, number]),
};

Card.defaultProps = {
  backgroundColor: v.white,
  className: undefined,
  component: 'section',
  href: undefined,
  onClick: noop,
  shadow: true,
  style: undefined,
  rounded: true,
  width: '100%',
};

export default Card;
